import React from 'react'
import { ConfigProps, InjectedFormProps, reduxForm } from 'redux-form'
import { Tabs } from '@cbgms/components/form/Tabs'
import FormButtons, { IFormButtonsProps } from './FormButtons'
import { FormLayout, withFormHandlerMapping } from './FormLayout'
import { compose } from '@cbgms/base/utils/compose'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import withDeleteModal, { DeleteModalProps } from './WithDeleteModal'
import withSaveConfirmationModal, { WithSaveConfirmationModalOuterProps } from './WithSaveConfirmationModal'
interface ITabbedFormOuterProps {
  showButtons?: boolean
  translationKey?: string
  onDelete?: (dryRun: boolean) => void
  hideSubmitAndDone?: boolean
  useMaterial?: boolean
  [key: string]: any
}

export interface TabbedFormOuterProps extends ITabbedFormOuterProps, ConfigProps<any, any, any> {}

// eslint-disable-next-line carsys-custom/typescript-prefer-interface-extend-over-intersection
export type TabbedFormProps = TabbedFormOuterProps &
  InjectedFormProps &
  RouteComponentProps<{}> &
  DeleteModalProps &
  WithSaveConfirmationModalOuterProps &
  // withSaveConfirmationModal will overwrite the onSubmit with a handleSubmit(onSubmit) of redux from
  Pick<IFormButtonsProps, 'onSubmit' | 'onSubmitAndDone' | 'onCancel'>

const enhance = compose<TabbedFormProps, TabbedFormOuterProps>(
  withRouter,
  withFormHandlerMapping(),
  withDeleteModal,
  // Do not touch keepDirtyOnReinitialize or enableReinitialize!!! Check FormLayout for an explanation
  reduxForm({ enableReinitialize: false, keepDirtyOnReinitialize: false }),
  withSaveConfirmationModal()
)

/**
 * @deprecated move away from redux-form tabs, use/introduce components from @carsys/form
 * */
export const TabbedForm: React.StatelessComponent<TabbedFormProps> = props => {
  const {
    children,
    handleSubmit,
    onDelete,
    match,
    onCancel,
    onSubmit,
    onSubmitAndDone,
    onSubmitSuccess,
    pristine,
    submitting,
    form,
    deleteReasons,
    handleConfirmSubmit,
    showButtons,
    useMaterial,
    handleShopChange,
    handleLogout,
    isPortal,
    ...rest
  } = props

  const validateTabbedForm = async () => {
    const { syncErrors, asyncErrors, submitErrors } = props || {}
    let errs = { ...(syncErrors ?? {}), ...(asyncErrors ?? {}), ...(submitErrors ?? {}) }

    const asyncValidateForm = props?.asyncValidate
    if (typeof asyncValidateForm === 'function') {
      try {
        await asyncValidateForm()
      } catch {
        const latestAsync = props?.asyncErrors ?? {}
        errs = { ...errs, ...latestAsync }
      }
    }
    return errs
  }

  const submitTabbedForm = async () => {
    const { handleSubmit, onSubmit } = props || {}
    if (typeof onSubmit !== 'function') return
    await new Promise<void>((resolve, reject) => {
      const runner = handleSubmit(async (values, dispatch, componentProps) => {
        try {
          await onSubmit(values, dispatch, componentProps)
          resolve()
        } catch (e) {
          reject(e)
          throw e
        }
      })
      runner()
    })
  }

  return (
    <FormLayout
      form={form}
      pristine={pristine}
      submitting={submitting}
      handleSubmit={handleSubmit}
      handleConfirmSubmit={handleConfirmSubmit}
      match={match}
      useMaterial={useMaterial}
      handleShopChange={handleShopChange}
      handleLogout={handleLogout}
      isPortal={isPortal}
      validateTabbedForm={validateTabbedForm}
      submitTabbedForm={submitTabbedForm}
      {...rest}
    >
      <Tabs
        buttons={
          showButtons ? (
            <FormButtons
              onCancel={onCancel}
              onDelete={onDelete}
              onSubmit={onSubmit}
              onSubmitAndDone={
                typeof onSubmitAndDone === 'function' && !props.hideSubmitAndDone ? handleConfirmSubmit(onSubmitAndDone) : undefined
              }
              pristine={pristine}
              submitting={submitting}
            />
          ) : undefined
        }
        formName={form}
      >
        {children}
      </Tabs>
    </FormLayout>
  )
}

TabbedForm.defaultProps = {
  showButtons: true
}

/**
 * @deprecated move away from redux-form tabs, use/introduce components from @carsys/form
 * */
export default enhance(TabbedForm)
